/* eslint-disable */
import React from 'react';
import mapContext from '../contexts/map-context';
import { ActionTypes } from '../contexts/reducers/map-reducer';
import { getServices } from '../utils/search';
import type { Dealer } from '../models/dealer';
import useSearchByTerm from './useSearchByTerm';
import useLazySearchByAddress from './useLazySearchByAddress';
import { checkIfPostalCode } from '../utils/checkIfPostalCode';
import {
  trackSearchByTermQuery,
  trackSearchByGeolocationQuery,
  trackSearchByZipCodeQuery,
} from '../utils/trackingUtils';
import trackingContext from '../contexts/tracking-context';
import { getRadiusInKm } from '../utils/distance';
import { getActiveFilters } from '../utils/activeFilters';

export default function useResultsByTermAndAddress() {
  const { trackingService: tService } = trackingContext.useTrackingState();
  const dispatch = mapContext.useMapDispatch();
  const isFirstLoadRef = React.useRef(true);

  const {
    distanceUnit,
    intlSearchEnabled,
    locale,
    placeId,
    preselectedFilters,
    query,
    searchError,
    searchGeoLocation,
    searchRadius,
    userGeolocation,
    userSelectedFilters,
    results,
  } = mapContext.useMapState();

  const activeFilters = getActiveFilters(preselectedFilters, userSelectedFilters);

  const {
    loading: termLoading,
    error: termError,
    dealersResult: termDealersResult,
  } = useSearchByTerm(query, intlSearchEnabled ? undefined : locale.countryCode, activeFilters);

  const {
    loadDealers: loadDealersByAddress,
    loading: addressLoading,
    error: addressError,
    dealersResult: addressDealersResult,
  } = useLazySearchByAddress(
    query,
    getRadiusInKm(searchRadius, distanceUnit),
    !intlSearchEnabled || checkIfPostalCode(query, locale.countryCode)
      ? locale.countryCode
      : undefined,
    activeFilters,
    userGeolocation?.lat,
    userGeolocation?.lng,
  );

  const dispatchSuccessfulQueries = (dealers: Dealer[]) => {
    dispatch({
      type: ActionTypes.setLoading,
      payload: false,
    });
    dispatch({
      type: ActionTypes.setSearchError,
      payload: null,
    });
    dispatch({
      type: ActionTypes.setResults,
      payload: { dealers, services: getServices(dealers) },
    });
    dispatch({
      type: ActionTypes.setSelectedPartner,
      payload: { tenant: dealers[0]?.country, kvpsid: dealers[0]?.kvpsId },
    });
  };

  React.useEffect(() => {
    if (!isFirstLoadRef.current) return;

    isFirstLoadRef.current = false;
  }, []);

  React.useEffect(() => {
    if (query?.length > 0 && checkIfPostalCode(query, locale.countryCode)) {
      loadDealersByAddress();
    }
  }, [query]);

  React.useEffect(() => {
    if (termDealersResult?.length === 0 || !results) loadDealersByAddress();
  }, [searchRadius, termDealersResult]);

  React.useEffect(() => {
    loadDealersByAddress();
  }, [searchRadius]);

  React.useEffect(() => {
    if (!searchError && addressError && !query) {
      dispatch({
        type: ActionTypes.setSearchError,
        payload: addressError,
      });
      dispatch({
        type: ActionTypes.setResults,
        payload: { dealers: [], services: [] },
      });
      dispatch({
        type: ActionTypes.setSelectedPartner,
        payload: { tenant: '', kvpsid: '' },
      });
      dispatch({
        type: ActionTypes.setLoading,
        payload: false,
      });
      dispatch({ type: ActionTypes.resetGeolocationSearch, payload: null });
    }
  }, [addressError, query]);

  React.useEffect(() => {
    if (addressError && termDealersResult?.length !== 0) {
      dispatch({
        type: ActionTypes.setSearchError,
        payload: addressError,
      });
      dispatch({
        type: ActionTypes.setSelectedPartner,
        payload: { tenant: '', kvpsid: '' },
      });
      dispatch({
        type: ActionTypes.setLoading,
        payload: false,
      });
      dispatch({ type: ActionTypes.resetGeolocationSearch, payload: null });
    }
    if (termDealersResult?.length === 0 && addressError) {
      dispatch({
        type: ActionTypes.setResults,
        payload: { dealers: [], services: [] },
      });
      dispatch({
        type: ActionTypes.setSearchError,
        payload: addressError,
      });
      dispatch({
        type: ActionTypes.setSelectedPartner,
        payload: { tenant: '', kvpsid: '' },
      });
      dispatch({
        type: ActionTypes.setLoading,
        payload: false,
      });
    }
  }, [addressError, termDealersResult]);

  React.useEffect(() => {
    if (termError || termDealersResult?.length === 0) {
      dispatch({
        type: ActionTypes.setSearchError,
        payload: termError,
      });
    }
  }, [termError]);

  React.useEffect(() => {
    if (
      (termLoading && termDealersResult?.length === 0) ||
      (addressLoading && addressDealersResult?.length === 0)
    ) {
      dispatch({
        type: ActionTypes.setLoading,
        payload: true,
      });
      dispatch({ type: ActionTypes.setSearchRadiusExtended, payload: false });
    } else
      dispatch({
        type: ActionTypes.setLoading,
        payload: false,
      });
  }, [termLoading, addressLoading]);

  React.useEffect(() => {
    if ((termDealersResult && !addressLoading) || (termDealersResult && !isFirstLoadRef.current)) {
      if (termDealersResult.length && !placeId && !results) {
        dispatchSuccessfulQueries(termDealersResult);
        dispatch({
          type: ActionTypes.setShowRadius,
          payload: searchGeoLocation.lat !== 0 && searchGeoLocation.lng !== 0,
        });
        trackSearchByTermQuery(
          tService,
          query,
          termDealersResult.length,
          activeFilters,
          termDealersResult,
        );
      } else {
        loadDealersByAddress();
      }
    }
  }, [termDealersResult]);

  React.useEffect(() => {
    if (addressDealersResult && query && !results) {
      dispatchSuccessfulQueries(addressDealersResult);
      dispatch({ type: ActionTypes.setShowRadius, payload: true });
      if (Number.isNaN(parseInt(query, 10))) {
        trackSearchByGeolocationQuery(
          tService,
          query,
          addressDealersResult.length,
          activeFilters,
          addressDealersResult,
        );
        return;
      }
      trackSearchByZipCodeQuery(
        tService,
        query,
        addressDealersResult?.length || 0,
        activeFilters,
        addressDealersResult || [],
      );
    }
  }, [addressDealersResult, query]);
}
